import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	FormField,
	Input,
	Modal,
	Page,
	Select,
	Table,
	type TableColumn,
	Tabs,
	useToast,
} from "@theg1group/ui";
import { useState } from "react";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDate, fmtNum } from "../lib/format.ts";
import type { ChainInfo, LabelInput, LabelRow, LabelSource, Rows } from "../lib/types.ts";

const CATEGORIES = [
	"exchange",
	"mixer",
	"sanctioned",
	"scam",
	"gambling",
	"bridge",
	"dex",
	"defi",
	"payment_processor",
	"merchant",
	"miner",
	"other",
];
const SEVERITIES = ["", "low", "medium", "high", "critical"];

export function Labels() {
	const [tab, setTab] = useState("labels");
	return (
		<Page title="Labels" description="Known-entity tags from public feeds plus manual overrides.">
			<Tabs
				activeTab={tab}
				onTabChange={setTab}
				tabs={[
					{ id: "labels", label: "Labels", content: <LabelsTab /> },
					{ id: "sources", label: "Sources", content: <SourcesTab /> },
				]}
			/>
		</Page>
	);
}

function LabelsTab() {
	const qc = useQueryClient();
	const toast = useToast();
	const [q, setQ] = useState("");
	const [category, setCategory] = useState("");
	const [source, setSource] = useState("");
	const [chain, setChain] = useState("");
	const [showAdd, setShowAdd] = useState(false);

	const chains = useQuery({
		queryKey: ["chains"],
		queryFn: () => api.get<ChainInfo[]>("/api/wallets/chains"),
	});
	const sources = useQuery({
		queryKey: ["label-sources"],
		queryFn: () => api.get<Rows<LabelSource>>("/api/labels/sources"),
	});
	const list = useQuery({
		queryKey: ["labels", q, category, source, chain],
		queryFn: () => {
			const p = new URLSearchParams({ q, category, source, chain });
			return api.get<Rows<LabelRow>>(`/api/labels?${p.toString()}`);
		},
	});
	const del = useMutation({
		mutationFn: (id: string) => api.del(`/api/labels/${id}`),
		onSuccess: () => {
			toast.addToast("success", "Label removed");
			qc.invalidateQueries({ queryKey: ["labels"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const cols: TableColumn<LabelRow>[] = [
		{ id: "chain", header: "Chain", accessor: (r) => <ChainBadge chain={r.chain} /> },
		{
			id: "address",
			header: "Address",
			accessor: (r) => <AddressLink address={r.address} display={r.display} head={10} tail={6} />,
		},
		{
			id: "category",
			header: "Category",
			accessor: (r) => <Badge variant="warning">{r.category}</Badge>,
		},
		{ id: "entity", header: "Entity", accessor: (r) => r.entity ?? "—" },
		{ id: "severity", header: "Severity", accessor: (r) => <RiskBadge score={r.severity} /> },
		{
			id: "conf",
			header: "Conf",
			accessor: (r) => (r.confidence === null ? "—" : Number(r.confidence).toFixed(2)),
		},
		{
			id: "stop",
			header: "Stop",
			accessor: (r) =>
				r.stopExpansion ? (
					<Badge variant="error" size="sm">
						stop
					</Badge>
				) : (
					"—"
				),
		},
		{
			id: "source",
			header: "Source",
			accessor: (r) => <span className="text-xs text-muted">{r.sourceId}</span>,
		},
		{
			id: "fetched",
			header: "Fetched",
			accessor: (r) => <span title={fmtDate(r.fetchedAt)}>{fmtAgo(r.fetchedAt)}</span>,
		},
		{
			id: "actions",
			header: "",
			accessor: (r) =>
				r.sourceId === "manual" ? (
					<Button size="sm" variant="danger" onClick={() => del.mutate(r.id)}>
						Delete
					</Button>
				) : null,
		},
	];

	return (
		<>
			<div className="mb-3 flex flex-wrap gap-2 items-center">
				<div className="w-72">
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search address / entity…"
						className="font-mono"
						size="sm"
					/>
				</div>
				<div className="w-44">
					<Select
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						options={[
							{ value: "", label: "All categories" },
							...CATEGORIES.map((c) => ({ value: c, label: c })),
						]}
						size="sm"
					/>
				</div>
				<div className="w-44">
					<Select
						value={source}
						onChange={(e) => setSource(e.target.value)}
						options={[
							{ value: "", label: "All sources" },
							...(sources.data?.rows ?? []).map((s) => ({ value: s.id, label: s.id })),
						]}
						size="sm"
					/>
				</div>
				<div className="w-40">
					<Select
						value={chain}
						onChange={(e) => setChain(e.target.value)}
						options={[
							{ value: "", label: "All chains" },
							...(chains.data ?? []).map((c) => ({ value: c.id, label: c.name })),
						]}
						size="sm"
					/>
				</div>
				<div className="ml-auto">
					<Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
						Add label
					</Button>
				</div>
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={cols}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.id}
						loading={list.isLoading}
						emptyMessage="No labels match."
					/>
				</div>
			</Card>
			<AddLabelModal open={showAdd} onClose={() => setShowAdd(false)} chains={chains.data ?? []} />
		</>
	);
}

function AddLabelModal({
	open,
	onClose,
	chains,
}: { open: boolean; onClose: () => void; chains: ChainInfo[] }) {
	const qc = useQueryClient();
	const toast = useToast();
	const [chain, setChain] = useState("");
	const [address, setAddress] = useState("");
	const [category, setCategory] = useState("scam");
	const [entity, setEntity] = useState("");
	const [severity, setSeverity] = useState("");
	const [stop, setStop] = useState(false);

	const add = useMutation({
		mutationFn: (body: LabelInput) => api.post("/api/labels", body),
		onSuccess: () => {
			toast.addToast("success", "Label added");
			qc.invalidateQueries({ queryKey: ["labels"] });
			setAddress("");
			setEntity("");
			onClose();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const effectiveChain = chain || chains[0]?.id || "";

	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Add manual label"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!effectiveChain || !address.trim() || !category}
						loading={add.isPending}
						onClick={() =>
							add.mutate({
								chain: effectiveChain,
								address: address.trim(),
								category,
								entity: entity.trim() || undefined,
								severity: severity || undefined,
								stopExpansion: stop,
							})
						}
					>
						Add
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<FormField label="Chain" htmlFor="lb-chain">
					<Select
						id="lb-chain"
						value={effectiveChain}
						onChange={(e) => setChain(e.target.value)}
						options={chains.map((c) => ({ value: c.id, label: c.name }))}
					/>
				</FormField>
				<FormField label="Address" htmlFor="lb-address">
					<Input
						id="lb-address"
						value={address}
						onChange={(e) => setAddress(e.target.value)}
						className="font-mono"
						spellCheck={false}
					/>
				</FormField>
				<FormField label="Category" htmlFor="lb-cat">
					<Select
						id="lb-cat"
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						options={CATEGORIES.map((c) => ({ value: c, label: c }))}
					/>
				</FormField>
				<FormField label="Entity" htmlFor="lb-entity" hint="e.g. Binance hot wallet">
					<Input id="lb-entity" value={entity} onChange={(e) => setEntity(e.target.value)} />
				</FormField>
				<FormField label="Severity" htmlFor="lb-sev">
					<Select
						id="lb-sev"
						value={severity}
						onChange={(e) => setSeverity(e.target.value)}
						options={SEVERITIES.map((s) => ({ value: s, label: s || "—" }))}
					/>
				</FormField>
				<label className="flex items-center gap-2 text-sm text-content cursor-pointer">
					<input type="checkbox" checked={stop} onChange={(e) => setStop(e.target.checked)} />
					Stop expansion here (traces won't walk through this address)
				</label>
			</div>
		</Modal>
	);
}

function SourcesTab() {
	const qc = useQueryClient();
	const toast = useToast();
	const list = useQuery({
		queryKey: ["label-sources"],
		queryFn: () => api.get<Rows<LabelSource>>("/api/labels/sources"),
	});
	const refresh = useMutation({
		mutationFn: (id: string) => api.post(`/api/labels/sources/${id}/refresh`),
		onSuccess: (_r, id) => {
			toast.addToast("success", `Refresh of ${id} queued`);
			qc.invalidateQueries({ queryKey: ["label-sources"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const cols: TableColumn<LabelSource>[] = [
		{
			id: "id",
			header: "Source",
			accessor: (r) => (
				<div>
					<div className="text-content font-medium">{r.id}</div>
					{r.url && (
						<a
							href={r.url}
							target="_blank"
							rel="noreferrer"
							className="text-[11px] text-primary hover:underline break-all"
						>
							{r.url}
						</a>
					)}
				</div>
			),
		},
		{ id: "license", header: "License", accessor: (r) => r.license ?? "—" },
		{
			id: "enabled",
			header: "Enabled",
			accessor: (r) => (r.enabled ? <Badge variant="success">on</Badge> : <Badge>off</Badge>),
		},
		{ id: "labels", header: "Labels", accessor: (r) => fmtNum(r.labelCount) },
		{
			id: "last",
			header: "Last fetched",
			accessor: (r) => <span title={fmtDate(r.lastFetchedAt)}>{fmtAgo(r.lastFetchedAt)}</span>,
		},
		{ id: "count", header: "Last count", accessor: (r) => fmtNum(r.lastCount) },
		{
			id: "error",
			header: "Last error",
			accessor: (r) =>
				r.lastError ? <span className="text-danger text-xs break-all">{r.lastError}</span> : "—",
		},
		{
			id: "actions",
			header: "",
			accessor: (r) => (
				<Button
					size="sm"
					variant="outline"
					disabled={!r.enabled}
					loading={refresh.isPending && refresh.variables === r.id}
					onClick={() => refresh.mutate(r.id)}
				>
					Refresh
				</Button>
			),
		},
	];

	return (
		<Card>
			<div className="overflow-x-auto">
				<Table
					columns={cols}
					data={list.data?.rows ?? []}
					keyExtractor={(r) => r.id}
					loading={list.isLoading}
					emptyMessage="No label sources configured."
				/>
			</div>
		</Card>
	);
}
